import { useState, type ReactNode } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  ACCOUNTS,
  CATEGORIAS_GASTO,
  type CategoriaGasto,
  type Deposito,
  type Gasto,
  type MovimientoDueno,
  type PagoProveedor,
  type TransferenciaBanco,
} from '@abastece/core';
import { DEFAULT_SETTINGS, centavos, esCuentaEnDolares, fechaEnNicaragua, formatMoney, newId, parseMoney, type Centavos } from '@abastece/shared';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { nombreDeCuenta } from '../lib/bancos.js';
import { emitirYVer, probarEvento } from '../lib/emitir.js';
import { useT, type Clave } from '../i18n/index.js';
import { etiquetaCategoriaGasto } from '../i18n/dominio.js';
import { Campo, Selector } from './Campo.js';
import { enDolares, fechaCorta } from './CuentasDeCaja.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';
import { PedirPin, usePinParaAnular } from './PedirPin.js';

/**
 * Putting right what somebody forgot to write down (the owner asked, 2026-10-03, what cures a step
 * nobody took): a shortage explained as what it really went on, and a deposit, money for or from
 * the owner, a supplier's payment or an expense voided. Each with the owner's own PIN, and each
 * only while no count has found the money since: the books refuse it after (core `contadas.ts`),
 * and the sheet says so as it opens.
 */

const FALTANTES = [ACCOUNTS.FALTANTE_CAJA.code, ACCOUNTS.FALTANTE_GENERAL.code];

/** What each kind of count is called, by its event. */
const CONTEO: Record<string, Clave> = {
  'caja.cerrada': 'explicar.conteo.corte',
  'caja.entregada': 'explicar.conteo.entrega',
  'caja.abierta': 'explicar.conteo.apertura',
  'efectivo.recibido': 'explicar.conteo.recibido',
  'efectivo.arqueo': 'explicar.conteo.arqueo',
};

/**
 * What a count found missing, and what of it the owner has explained: the books' own figures, its
 * entries on the shortage accounts, and the expenses that explain it, voided ones aside.
 */
export async function faltanteDelConteo(eventoId: string): Promise<{ falto: Centavos; explicado: Centavos; fecha: string | null }> {
  const [asientos, explicaciones, config] = await Promise.all([
    db.asientos.where('eventId').equals(eventoId).toArray(),
    db.gastos.filter((g) => g.faltanteDe === eventoId && !g.anulado).toArray(),
    readSettings(),
  ]);
  // A till's count the owner corrected found what the books now keep of it (`conteosCorregidos`).
  const corregido = config.conteosCorregidos[eventoId];
  const falto =
    corregido !== undefined
      ? -corregido
      : asientos.flatMap((a) => a.lineas).reduce((s, l) => s + (FALTANTES.includes(l.cuenta) ? l.debe - l.haber : 0), 0);
  // One that booked nothing until it was corrected is on its own day all the same.
  const conteo = asientos.length === 0 ? await db.eventos.get(eventoId) : undefined;
  return {
    falto: centavos(Math.max(0, falto)),
    explicado: centavos(explicaciones.reduce((s, g) => s + g.montoCordobas, 0)),
    fecha: asientos[0]?.fecha ?? (conteo ? fechaEnNicaragua(conteo.clientTs) : null),
  };
}

/**
 * A shortage explained: some or all of what a count found missing went on an expense nobody wrote
 * down. It becomes that expense on the count's day, and stops being anybody's shortage; no money
 * moves, since the count already took it out of the cash (`faltante.explicado`). What is left
 * unexplained is offered, and nothing past it is taken.
 */
export function ExplicarFaltante({ eventoId, nombre, onCerrar }: { eventoId: string; nombre?: string; onCerrar: () => void }) {
  const t = useT();
  const pin = usePinParaAnular(false);
  const datos = useLiveQuery(async () => {
    const [conteo, evento, explicaciones] = await Promise.all([
      faltanteDelConteo(eventoId),
      db.eventos.get(eventoId),
      db.gastos.filter((g) => g.faltanteDe === eventoId && !g.anulado).toArray(),
    ]);
    return { ...conteo, tipo: evento?.type ?? '', cuando: evento?.clientTs ?? null, explicaciones };
  }, [eventoId]);
  const [monto, setMonto] = useState<string | null>(null);
  const [categoria, setCategoria] = useState<CategoriaGasto>('otros');
  const [descripcion, setDescripcion] = useState('');
  const [pidiendoPin, setPidiendoPin] = useState(false);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hecho, setHecho] = useState<{ monto: Centavos; categoria: CategoriaGasto } | null>(null);

  if (!datos) return null;
  const queda = centavos(Math.max(0, datos.falto - datos.explicado));
  // The amount starts at what is left: most of the time it all went on one thing.
  const escrito = monto ?? (queda > 0 ? (queda / 100).toFixed(2) : '');
  const valor = parseMoney(escrito);
  const demasiado = valor !== null && valor > queda;
  const listo = valor !== null && valor > 0 && !demasiado && descripcion.trim() !== '' && !trabajando;
  const fecha = datos.fecha ?? (datos.cuando ? fechaEnNicaragua(datos.cuando) : null);
  const conteo = t(CONTEO[datos.tipo] ?? 'explicar.conteo.otro', { fecha: fecha ? fechaCorta(fecha) : '—' });

  async function explicar() {
    if (!listo || valor === null) return;
    setPidiendoPin(false);
    setTrabajando(true);
    setError(null);
    try {
      const { rechazo } = await emitirYVer('faltante.explicado', {
        gastoId: newId(),
        eventoId,
        categoria,
        descripcion: descripcion.trim(),
        monto: valor,
      });
      if (rechazo) setError(t('explicar.noSeExplico', { motivo: rechazo }));
      else setHecho({ monto: valor, categoria });
    } catch (e) {
      setError(e instanceof Error ? e.message : t('explicar.noSeExplico', { motivo: '' }));
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <Modal
      titulo={t('explicar.titulo')}
      onCerrar={onCerrar}
      pie={
        hecho || queda === 0 ? (
          <button type="button" className="btn-primario w-full" onClick={onCerrar}>
            <Ico icono="listo" />
            {t('comun.listo')}
          </button>
        ) : (
          <button type="button" className="btn-primario w-full" disabled={!listo} onClick={() => setPidiendoPin(true)} data-explicar-guardar>
            <Ico icono="guardar" />
            {t('explicar.guardar')}
          </button>
        )
      }
    >
      <div className="flex flex-col gap-4" data-explicar-faltante>
        <div className="tarjeta flex flex-col gap-1.5">
          <p className="text-sm">
            {nombre
              ? t('explicar.faltoA', { conteo, monto: formatMoney(datos.falto), nombre })
              : t('explicar.falto', { conteo, monto: formatMoney(datos.falto) })}
          </p>
          {datos.explicaciones.map((g) => (
            <p key={g.id} className="text-xs text-slate-400">
              {t('explicar.yaExplicado', { monto: formatMoney(g.montoCordobas), descripcion: g.descripcion })}
            </p>
          ))}
          {datos.explicado > 0 && queda > 0 && (
            <p className="text-sm font-medium text-amber-200">{t('explicar.queda', { monto: formatMoney(queda) })}</p>
          )}
        </div>
        {hecho ? (
          <p className="rounded-xl bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200" data-explicar-listo>
            {t('explicar.listo', { monto: formatMoney(hecho.monto), categoria: etiquetaCategoriaGasto(hecho.categoria) })}
          </p>
        ) : queda === 0 ? (
          <p className="text-sm text-slate-400">{t('explicar.nada')}</p>
        ) : (
          <>
            <p className="text-sm text-slate-400">{t('explicar.ayuda')}</p>
            <Campo
              etiqueta={t('explicar.monto')}
              inputMode="decimal"
              className="tabular text-lg"
              value={escrito}
              onChange={(e) => setMonto(e.target.value)}
              ayuda={t('explicar.hasta', { monto: formatMoney(queda) })}
              error={demasiado ? t('explicar.demasiado', { monto: formatMoney(queda) }) : undefined}
            />
            <Selector etiqueta={t('gastos.categoria')} value={categoria} onChange={(e) => setCategoria(e.target.value as CategoriaGasto)}>
              {CATEGORIAS_GASTO.map((c) => (
                <option key={c} value={c}>
                  {etiquetaCategoriaGasto(c)}
                </option>
              ))}
            </Selector>
            <Campo
              etiqueta={t('explicar.descripcion')}
              placeholder={t('explicar.descripcionEjemplo')}
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              maxLength={200}
              autoComplete="off"
            />
            {error && <p className="text-sm text-rose-400">{error}</p>}
          </>
        )}
      </div>
      {pidiendoPin && valor !== null && (
        <PedirPin
          titulo={pin.titulo}
          motivo={t('explicar.pin', { monto: formatMoney(valor), descripcion: descripcion.trim() })}
          verificar={pin.verificar}
          donde={pin.donde}
          onCancelar={() => setPidiendoPin(false)}
          onAutorizado={() => void explicar()}
        />
      )}
    </Modal>
  );
}

/** What can be voided from here, and the event that voids it, less its reason. */
export type Anulacion =
  | { type: 'efectivo.deposito_anulado'; payload: { depositoId: string } }
  | { type: 'dueno.movimiento_anulado'; payload: { movimientoId: string } }
  | { type: 'compra.pago_anulado'; payload: { compraId: string; pagoId: string } }
  | { type: 'gasto.pago_anulado'; payload: { gastoId: string; pagoId: string } }
  | { type: 'gasto.anulado'; payload: { gastoId: string; sinContar: true } }
  | { type: 'banco.transferencia_anulada'; payload: { transferenciaId: string } };

/**
 * Voiding a record: what it is, what voiding it does, why it is voided, and the owner's own PIN.
 * As it opens it asks this phone's books whether they would take it (`probarEvento`), and one past
 * voiding says why instead of asking for a reason and a PIN for nothing.
 */
export function Anular({
  titulo,
  que,
  efecto,
  anulacion,
  onCerrar,
}: {
  titulo: string;
  /** The record, as a line or two. */
  que: ReactNode;
  /** What happens to the money. */
  efecto: string;
  anulacion: Anulacion;
  onCerrar: () => void;
}) {
  const t = useT();
  const pin = usePinParaAnular(false);
  const [motivo, setMotivo] = useState('');
  const [pidiendoPin, setPidiendoPin] = useState(false);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [anulado, setAnulado] = useState(false);
  const clave = JSON.stringify(anulacion);
  // Asked again as the books change, and no more once voided: then they would say it already is.
  const noSePuede = useLiveQuery(
    async () => (anulado ? null : probarEvento(anulacion.type, { ...anulacion.payload, motivo: '·' } as never)),
    [clave, anulado],
  );

  async function anular() {
    setPidiendoPin(false);
    setTrabajando(true);
    setError(null);
    try {
      const { rechazo } = await emitirYVer(anulacion.type, { ...anulacion.payload, motivo: motivo.trim() } as never);
      if (rechazo) setError(t('anular.noSeAnulo', { motivo: rechazo }));
      else setAnulado(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('anular.noSeAnulo', { motivo: '' }));
    } finally {
      setTrabajando(false);
    }
  }

  const cerrar = anulado || (noSePuede !== undefined && noSePuede !== null);
  return (
    <Modal
      titulo={titulo}
      onCerrar={onCerrar}
      pie={
        cerrar ? (
          <button type="button" className="btn-primario w-full" onClick={onCerrar}>
            <Ico icono="listo" />
            {t('comun.listo')}
          </button>
        ) : (
          <button
            type="button"
            className="btn-primario w-full"
            disabled={noSePuede !== null || !motivo.trim() || trabajando}
            onClick={() => setPidiendoPin(true)}
            data-anular-confirmar
          >
            <Ico icono="anular" />
            {t('comun.anular')}
          </button>
        )
      }
    >
      <div className="flex flex-col gap-4" data-anular>
        <div className="tarjeta text-sm">{que}</div>
        {anulado ? (
          <p className="rounded-xl bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200" data-anular-listo>
            {t('anular.listo')}
          </p>
        ) : noSePuede ? (
          <p className="rounded-xl bg-amber-500/10 px-4 py-3 text-sm text-amber-200" data-anular-no-se-puede>
            {t('anular.noSePuede', { motivo: noSePuede })}
          </p>
        ) : (
          <>
            <p className="text-sm text-slate-400">{efecto}</p>
            <Campo
              etiqueta={t('anular.porQue')}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              maxLength={200}
              autoComplete="off"
              autoFocus
            />
            {error && <p className="text-sm text-rose-400">{error}</p>}
          </>
        )}
      </div>
      {pidiendoPin && (
        <PedirPin
          titulo={pin.titulo}
          motivo={t('anular.pin', { titulo })}
          verificar={pin.verificar}
          donde={pin.donde}
          onCancelar={() => setPidiendoPin(false)}
          onAutorizado={() => void anular()}
        />
      )}
    </Modal>
  );
}

/** An expense voided: one never paid, or written down twice; an explanation, a shortage again. */
export function AnularGasto({ gasto: g, onCerrar }: { gasto: Gasto; onCerrar: () => void }) {
  const t = useT();
  return (
    <Anular
      titulo={t('anular.gasto')}
      que={
        <>
          <p className="font-medium">{g.descripcion}</p>
          <p className="text-xs text-slate-500">
            {etiquetaCategoriaGasto(g.categoria)} · {fechaCorta(g.fecha)}
          </p>
          <p className="tabular text-slate-300">{formatMoney(g.montoCordobas)}</p>
        </>
      }
      efecto={t(g.faltanteDe ? 'anular.efectoExplicacion' : 'anular.efectoGasto')}
      anulacion={{ type: 'gasto.anulado', payload: { gastoId: g.id, sinContar: true } }}
      onCerrar={onCerrar}
    />
  );
}

/** A deposit voided: one that was never made, or was written down twice. */
export function AnularDeposito({ deposito: d, onCerrar }: { deposito: Deposito; onCerrar: () => void }) {
  const t = useT();
  // Cash brought from the bank is kept, and voided, with the deposits: back to the bank, then.
  const delBanco = d.delBanco === true;
  return (
    <Anular
      titulo={t(delBanco ? 'anular.sacado' : 'anular.deposito')}
      que={
        <>
          <p className="font-medium">{t(delBanco ? 'anular.sacadoDel' : 'anular.depositoDel', { fecha: fechaCorta(d.fecha) })}</p>
          <p className="tabular text-slate-300">
            {[d.cordobas > 0 ? formatMoney(d.cordobas) : null, d.dolares > 0 ? enDolares(d.dolares) : null].filter(Boolean).join(' · ')}
          </p>
          {d.referencia && <p className="text-xs text-slate-500">{t('anular.boleta', { referencia: d.referencia })}</p>}
        </>
      }
      efecto={t(delBanco ? 'anular.efectoSacado' : 'anular.efectoDeposito')}
      anulacion={{ type: 'efectivo.deposito_anulado', payload: { depositoId: d.id } }}
      onCerrar={onCerrar}
    />
  );
}

/** Money moved from one bank account to another voided: it goes back to the one it left. */
export function AnularTransferenciaBanco({ transferencia: x, onCerrar }: { transferencia: TransferenciaBanco; onCerrar: () => void }) {
  const t = useT();
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  const enSuMoneda = (cuenta: string, c: Centavos) => (esCuentaEnDolares(cuenta) ? enDolares(c) : formatMoney(c));
  return (
    <Anular
      titulo={t('anular.transferenciaBanco')}
      que={
        <>
          <p className="font-medium">
            {t('bancos.deA', { desde: nombreDeCuenta(config, x.desde, t), hacia: nombreDeCuenta(config, x.hacia, t) })} · {fechaCorta(x.fecha)}
          </p>
          <p className="tabular text-slate-300">
            {enSuMoneda(x.desde, x.monto)}
            {esCuentaEnDolares(x.desde) !== esCuentaEnDolares(x.hacia) && ` → ${enSuMoneda(x.hacia, x.recibido)}`}
          </p>
          {x.referencia && <p className="text-xs text-slate-500">{x.referencia}</p>}
        </>
      }
      efecto={t('anular.efectoTransferenciaBanco')}
      anulacion={{ type: 'banco.transferencia_anulada', payload: { transferenciaId: x.id } }}
      onCerrar={onCerrar}
    />
  );
}

/** Money for or from the owner voided. */
export function AnularMovimiento({ movimiento: m, onCerrar }: { movimiento: MovimientoDueno; onCerrar: () => void }) {
  const t = useT();
  const retiro = m.tipo === 'retiro';
  return (
    <Anular
      titulo={t(retiro ? 'anular.retiro' : 'anular.aporte')}
      que={
        <>
          <p className="font-medium">
            {t(retiro ? 'gastos.retiro' : 'gastos.aporte')} · {fechaCorta(m.fecha)}
          </p>
          <p className="tabular text-slate-300">{formatMoney(m.monto)}</p>
          {m.motivo && <p className="text-xs text-slate-500">{m.motivo}</p>}
        </>
      }
      efecto={t(retiro ? 'anular.efectoRetiro' : 'anular.efectoAporte')}
      anulacion={{ type: 'dueno.movimiento_anulado', payload: { movimientoId: m.id } }}
      onCerrar={onCerrar}
    />
  );
}

/** A payment to a supplier voided, of a purchase or of an expense bought on credit. */
export function AnularPago({
  deuda,
  pago,
  nombre,
  onCerrar,
}: {
  deuda: { tipo: 'compra' | 'gasto'; id: string };
  pago: PagoProveedor;
  /** Whom it was paid to. */
  nombre: string;
  onCerrar: () => void;
}) {
  const t = useT();
  return (
    <Anular
      titulo={t('anular.pago')}
      que={
        <>
          <p className="font-medium">{t('anular.pagoA', { nombre, fecha: fechaCorta(pago.fecha) })}</p>
          <p className="tabular text-slate-300">{formatMoney(pago.monto)}</p>
          {pago.referencia && <p className="text-xs text-slate-500">{pago.referencia}</p>}
        </>
      }
      efecto={t(pago.sesionId ? 'anular.efectoPagoDeGaveta' : 'anular.efectoPago')}
      anulacion={
        deuda.tipo === 'compra'
          ? { type: 'compra.pago_anulado', payload: { compraId: deuda.id, pagoId: pago.id } }
          : { type: 'gasto.pago_anulado', payload: { gastoId: deuda.id, pagoId: pago.id } }
      }
      onCerrar={onCerrar}
    />
  );
}
